import { Injectable, inject, Injector } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthStateService } from '@core/services/auth-state.service';
import { AuthService } from '@core/services/auth-new.service';
import { CompanyConfigService } from '@core/services/company-config.service';
import { StorageIntegrityService } from '@core/services/storage-integrity.service';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private readonly injector = inject(Injector);
  private readonly authStateService = inject(AuthStateService);
  private readonly companyConfigService = inject(CompanyConfigService);
  private readonly storageIntegrityService = inject(StorageIntegrityService);
  private readonly router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | boolean {
    // Lazy-resolve AuthService at call-time to avoid circular DI
    // (AuthService → HttpClient → HTTP_INTERCEPTORS → AuthInterceptor → ...)
    const authService = this.injector.get(AuthService);

    // ── Wait for auth initialization (token rehydration) before checking ─────
    return from(authService.whenReady).pipe(
      switchMap(() => from(this.storageIntegrityService.checkIntegrity(true))),
      switchMap(isIntegrityValid => {
        if (!isIntegrityValid) {
          // handleTampering() already redirected; block navigation.
          return of(false);
        }
        const authResult = this.performAuthCheck(route, state);
        // performAuthCheck may return boolean or Observable<boolean>
        return authResult instanceof Observable ? authResult : of(authResult);
      }),
      catchError(() => of(false))
    );
  }

  /** Original auth-check logic, extracted for clarity. */
  private performAuthCheck(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    try {
      // Primary check: AuthStateService (now properly initialized from storage)
      const isAuthenticated = this.authStateService.isAuthenticated();
      const isCustomerLogin = this.authStateService.isCustomerLogin();
      const currentCompany = this.authStateService.currentCompany() || 
                            localStorage.getItem('currentCompany') || 
                            'unity';
      
      console.log('Auth Guard Check:', {
        isAuthenticated,
        isCustomerLogin,
        currentCompany
      });
      
      // If AuthStateService says user is authenticated, verify token validity
      if (isAuthenticated && isCustomerLogin) {
        const tokenData = this.authStateService.getCompanyTokenData(currentCompany);

        if (!tokenData || !tokenData.token) {
          console.warn('No token data found, clearing auth state');
          this.logout();
          return false;
        }

        return true;
      }
      
      // Fallback check: try memory rehydration using cookie-based refresh
      const storedLoginStatus = JSON.parse(localStorage.getItem('isLoggedIn') || 'false');
      const storedCustomerLogin = JSON.parse(localStorage.getItem('isCustomerLogin') || 'false');
      const userDetailsKey = `tibss.${currentCompany}-userDetails`;
      
      const hasUserDetails = localStorage.getItem(userDetailsKey);
      
      if (storedLoginStatus && storedCustomerLogin && hasUserDetails) {
        // Token rehydration failed but localStorage still has session data.
        // Redirect to login without destroying session so user can re-authenticate.
        console.warn('Auth state not synchronized after initialization; redirecting to login');
        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url }
        });
        return false;
      }

      // Not authenticated - redirect to login
      console.log('User not authenticated, redirecting to login');
      this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url }
      });
      
      return false;
      
    } catch (error) {
      console.error('Error in auth guard:', error);
      this.router.navigate(['/auth/login']);
      return false;
    }
  }

  /**
   * Helper method to handle logout
   */
  private logout(): void {
    // Clear auth state
    this.authStateService.reset();

    // Clear integrity snapshot BEFORE clearing company data to prevent
    // false tampering detection from concurrent requests.
    this.storageIntegrityService.clearState();
    
    // Clear company configs
    this.companyConfigService.clearAll();
    
    // Navigate to login
    this.router.navigate(['/auth/login']);
  }
}
